import { useState, type CSSProperties } from 'react'
import { Plus, Loader2 } from 'lucide-react'

import SourceMenu, {
  ATTACH_FILE_ITEM, ATTACH_FILE_KEY,
  ATTACH_IOFLOW_ITEM, ATTACH_IOFLOW_KEY,
} from '@/components/sources/SourceMenu'
import type { SourceKind } from '@/components/sources/AddSourceModal'

/**
 * The unified "+" add control — ONE trigger button wired to the shared
 * SourceMenu — used by the Ask-AI dock, the builder hub and the describe-app
 * page so all three offer the identical "attach a file / add a data source"
 * affordance. They used to hand-roll separate paperclip ("Attach spec /
 * reference files") + "Add a source" buttons that drifted apart; this folds
 * both into the same single menu the dock's "+" already uses.
 *
 * The menu always LEADS with an "Attach file" row (what the paperclip button
 * used to do); every SOURCE_TYPES entry follows. The caller handles the two
 * outcomes: `onAttachFile()` (open the file picker) and `onSelectSource(kind)`
 * (open AddSourceModal for that source kind). This component owns the open
 * state and the trigger; the caller still owns the AddSourceModal + chips.
 */

interface Props {
  onSelectSource: (kind: SourceKind) => void
  onAttachFile: () => void
  /** Optional: offer "Attach IOFlow reference" too. Opt-in so the callers that
   *  have nothing to do with IOFlow keep the menu they had. */
  onAttachIOFlow?: () => void
  disabled?: boolean
  /** Swap the + for a spinner (e.g. while a draft project is being prepared). */
  loading?: boolean
  /** 'top' opens upward (docked composers), 'bottom' downward (pages). */
  placement?: 'top' | 'bottom'
  align?: 'left' | 'right'
  size?: 'sm' | 'md'
  title?: string
  /** Resting background — the dock (bg-secondary) and pages (bg) differ. */
  background?: string
  backgroundOpen?: string
  /** Sizing overrides merged onto the trigger (e.g. the dock fills its column). */
  buttonStyle?: CSSProperties
  /** Layout overrides for the positioning wrapper (e.g. the dock stretches it). */
  wrapperStyle?: CSSProperties
  iconSize?: number
}

export default function AddSourceButton({
  onSelectSource,
  onAttachFile,
  onAttachIOFlow,
  disabled = false,
  loading = false,
  placement = 'bottom',
  align = 'left',
  size = 'md',
  title = 'Attach a file, or add a data source to build on',
  background = 'var(--color-bg-secondary)',
  backgroundOpen = 'var(--color-bg-tertiary, var(--color-bg-secondary))',
  buttonStyle,
  wrapperStyle,
  iconSize = 16,
}: Props) {
  const [open, setOpen] = useState(false)

  return (
    <div style={{ position: 'relative', display: 'inline-flex', ...wrapperStyle }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        title={title}
        aria-haspopup="menu"
        aria-expanded={open}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 34,
          minWidth: 34,
          background: open ? backgroundOpen : background,
          color: 'var(--color-text-secondary)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-md)',
          cursor: disabled ? 'default' : 'pointer',
          ...buttonStyle,
        }}
      >
        {loading
          ? <Loader2 size={iconSize} style={{ animation: 'spin 1s linear infinite' }} />
          : <Plus size={iconSize} />}
      </button>
      <SourceMenu
        open={open}
        onClose={() => setOpen(false)}
        onSelect={(k) => {
          setOpen(false)
          if (k === ATTACH_FILE_KEY) onAttachFile()
          else if (k === ATTACH_IOFLOW_KEY) onAttachIOFlow?.()
          else onSelectSource(k as SourceKind)
        }}
        leadingItems={
          onAttachIOFlow ? [ATTACH_FILE_ITEM, ATTACH_IOFLOW_ITEM] : [ATTACH_FILE_ITEM]
        }
        placement={placement}
        align={align}
        size={size}
      />
    </div>
  )
}
